import { useState } from "react";
import {
  Activity, ArrowRight, BookOpen, Building2, CalendarDays, ChevronRight, ClipboardList, Clock,
  GraduationCap, Library, Lock, NotebookPen, PenLine, Pill, ShieldCheck, Sparkles,
  UserRound, Wind, Zap,
} from "lucide-react";
import SaludoDieciocho from "../home/SaludoDieciocho";
import BenitoAvatar from "../BenitoAvatar";
import Button from "../foundational/Button";
import { IDENTIDAD_MIPAE } from "../../constants/identidadMiPAE";
import {
  EJEMPLOS, HERRAMIENTAS, INDICE_CON_CUENTA, INDICE_SIN_CUENTA, fraseValores, momentoInicial,
} from "../../lib/presentacionMiPAE";
import { requiereSesion } from "../../lib/accessGate";
import { APPLE_LOGIN_DISPONIBLE } from "../../lib/nativeAuth";
import { trackLanding } from "../../lib/landingTracking";
import { isNative } from "../../lib/platform";
import { colorModulo } from "./colorModulo";
import { MomentosPestanas } from "./Momentos";
import { VinetaBenito } from "./Vinetas";
import { BloqueProposito, CENTRADO, SelloAlLlegar, TEXTO_2, TITULO_SECCION } from "./Identidad";
import { retraso, useAparecerAlBajar } from "./useAparecerAlBajar";
import { URL_TIENDA } from "../../lib/versionApp";
import { ENLACE_TEXTO, InvitacionInstitucional } from "./CanalesContacto";

// Fichas de MiPAE en las tiendas. La de Apple apunta a la tienda de Chile
// (la ficha en español); en un iPhone el enlace abre la App Store del aparato
// en el país de su cuenta, así que sirve igual fuera de Chile. Una sola
// fuente con la pantalla que pide actualizar (lib/versionApp.js).
export const URL_APP_STORE = URL_TIENDA.ios;
export const URL_GOOGLE_PLAY = URL_TIENDA.android;

// ─── Portada pública (visitante sin sesión) ──────────────────────────────────
// Rediseño 2026-09-29. La portada anterior presentaba la identidad como un
// documento: eslogan, un subtítulo abstracto y listas de tarjetas iguales.
// Ahora la página responde tres preguntas en orden:
//   1. ¿Qué es y por qué me sirve? El eslogan, una frase concreta de lo que
//      hace, la etapa del visitante (que ajusta el texto) y el acceso.
//   2. ¿Cómo se ve usarlo? Cuatro momentos de enfermería con un ejemplo del
//      producto cada uno (datos ficticios, rotulados).
//   3. ¿Puedo probar ya? Qué se abre sin cuenta y qué pide cuenta, derivado
//      de lib/accessGate para no prometer de más.
// El propósito y los valores cierran y llevan a «¿Qué es MiPAE?», donde
// viven completos.
//
// Pasada "menos Claude" (2026-09-29, pedido del fundador al ver la primera
// versión): sin rótulos en mayúsculas, sin cursivas, sin crema ni franja
// oscura, sin números ni píldoras, títulos de sección en la letra de la app
// y más espacio para el producto y para la cara de Benito.
//
// Pasada "con aire" (2026-09-29, el fundador en su teléfono: "se ve
// desproporcionado, le quita aire a la pantalla"): en el teléfono la
// portada vuelve a la escala de la app, la primera pantalla queda con el
// título, el texto, la etapa y el acceso (la tira de ejemplos se fue: el
// chat de Benito aparece justo abajo). La lista de lo que suma la cuenta se
// plegó y volvió a abrirse el mismo día: el fundador quiere que se vea lo
// que trae la cuenta, no solo lo que se abre sin ella.
// En pantallas anchas casi nada cambia.
//
// Centrada y con movimiento (2026-09-29, el fundador con su captura: "se ve
// descuadrado, fuera de centro"). En su teléfono la pantalla es más ancha que
// 390 px y las cajas de arriba (con ancho tope) quedaban cargadas a la
// izquierda mientras Benito iba centrado. Regla desde entonces: bajo lg todo
// va en UNA columna centrada del mismo ancho (COLUMNA), y los títulos,
// textos de portada y declaraciones van centrados; el contenido para leer
// (listas, momentos, viñetas) sigue alineado a la izquierda dentro de esa
// columna. Y pidió "más acción, más movimiento": los bloques entran al
// bajar (useAparecerAlBajar), las viñetas se arman en cascada y el chat de
// Benito se escribe solo. Todo bajo prefers-reduced-motion.
//
// El acceso (Apple, Google, correo) llega armado desde Home.jsx: esta pieza
// solo decide DÓNDE va. Por la guideline 4.8 de Apple, ninguna otra parte de
// la portada ofrece "Continuar con Google" suelto; los "Crear mi cuenta" de
// más abajo llevan a la tarjeta de acceso, donde Apple y Google van juntos.

const ETAPAS = [
  { id: "estudiante", label: "Estudiante" },
  { id: "interno", label: "Interno/a" },
  { id: "enfermero", label: "Enfermero/a" },
  { id: "tens", label: "TENS" },
];

const ICONO_HERRAMIENTA = {
  escalas: Activity,
  medicamentos: Pill,
  referencia: BookOpen,
  estudio: GraduationCap,
  "prepara-practica": BookOpen,
  "prepara-internado": BookOpen,
  "prepara-turno": Clock,
  respira: Wind,
  rapido: Sparkles,
  detallado: PenLine,
  turno: ClipboardList,
  calendario: CalendarDays,
  revisa: Zap,
  bitacora: NotebookPen,
  bibliografia: Library,
};


const reducirMovimiento = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// Ancho único de la columna del teléfono y la tableta: las cajas de la
// portada y el bloque de Benito comparten borde a cualquier ancho.
const COLUMNA = "mx-auto w-full max-w-[32rem] lg:mx-0 lg:max-w-[28rem]";

function FilaHerramienta({ id, onAbrir, orden = 0 }) {
  const h = HERRAMIENTAS[id];
  const c = colorModulo(h.modulo);
  const Icono = ICONO_HERRAMIENTA[id];
  const conCuenta = INDICE_CON_CUENTA.includes(id);
  return (
    <li className="pp-aparece" style={retraso(orden * 45)}>
      <button
        type="button"
        onClick={() => onAbrir(id)}
        className="group flex min-h-[56px] w-full items-center gap-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface rounded-tk-sm"
      >
        <span aria-hidden="true" className={`grid h-9 w-9 flex-shrink-0 place-items-center overflow-hidden rounded-tk-md ${c.tinte} ${c.icono}`}>
          {id === "benito" ? <BenitoAvatar size={28} /> : <Icono size={18} strokeWidth={1.7} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-body font-semibold leading-snug text-ink group-hover:text-accent">{h.nombre}</span>
          <span className="block text-small font-normal leading-snug text-ink-subtle dark:text-ink-muted">{h.descripcion}</span>
        </span>
        {conCuenta
          ? <Lock aria-label="Con cuenta" size={15} strokeWidth={2} className="flex-shrink-0 text-ink-muted" />
          : <ChevronRight aria-hidden="true" size={18} strokeWidth={1.8} className="flex-shrink-0 text-ink-muted group-hover:text-accent" />}
      </button>
    </li>
  );
}

export default function PortadaPublica({
  etapa,
  onElegirEtapa,
  landing,
  acceso,
  accesoVisible = true,
  onQueEsMiPAE,
  onContacto,
  onInstituciones,
  onDemo,
  onPrivacidad,
  abrirOLogin,
  onSelectMode,
  onAbrirEscalas,
  onAbrirMedicamentos,
  onAbrirReferencia,
  onAbrirEstudio,
  onPreparaPractica,
  onPreparaInternado,
  onPreparaTurno,
  onAbrirRespira,
  onAbrirBienestar,
  onAbrirRevisa,
  onAbrirBitacora,
  onAbrirBibliografia,
}) {
  // El momento que se muestra primero sigue a la etapa elegida; después la
  // persona puede recorrer los otros con las pestañas.
  const [momento, setMomento] = useState(() => momentoInicial(etapa));
  const raiz = useAparecerAlBajar();
  const elegirEtapa = (id) => {
    const siguiente = etapa === id ? null : id;
    onElegirEtapa(id);
    setMomento(momentoInicial(siguiente));
  };

  const irAAcceso = () => {
    const el = typeof document !== "undefined" ? document.getElementById("acceso-mipae") : null;
    if (!el) return;
    el.scrollIntoView?.({ behavior: reducirMovimiento() ? "auto" : "smooth", block: "center" });
    el.querySelector("button")?.focus({ preventScroll: true });
  };

  const verDemo = (etapaDemo) => {
    trackLanding("landing_cta", { etapa, cta: "demo" });
    onDemo?.(etapaDemo);
  };

  // Lo que pide sesión pasa por abrirOLogin (lib/accessGate), igual que en
  // la portada anterior: en la web y en Android abre Google directo. En la
  // app de iPhone NO: abrirOLogin termina en signInWithGoogle y Apple exige
  // que su botón esté al lado de Google (guideline 4.8, rechazo del
  // 16-sep), así que ahí se lleva a la tarjeta de acceso, que tiene los dos.
  const pedir = (id, fn) => (APPLE_LOGIN_DISPONIBLE && requiereSesion(id) ? irAAcceso() : abrirOLogin(id, fn));

  // Mismo cableado que tenían las tarjetas de la portada anterior. Bienestar
  // abre su módulo cuando App lo tiene encendido.
  const abrirHerramienta = (id) => {
    switch (id) {
      case "escalas":
        trackLanding("landing_cta", { etapa, cta: "escalas" });
        return pedir("escalas", onAbrirEscalas);
      case "medicamentos": return pedir("medicamentos", onAbrirMedicamentos);
      case "referencia": return pedir("referencia", onAbrirReferencia);
      case "estudio": return pedir("referencia", onAbrirEstudio);
      case "prepara-practica": return onPreparaPractica?.();
      case "prepara-internado":
        trackLanding("landing_cta", { etapa, cta: "prepara_internado" });
        return onPreparaInternado?.();
      case "prepara-turno": return onPreparaTurno?.();
      case "respira": return onAbrirBienestar ? onAbrirBienestar() : onAbrirRespira?.();
      case "rapido": return pedir("rapido", () => onSelectMode?.("rapido"));
      case "detallado": return pedir("detallado", () => onSelectMode?.("detallado"));
      case "revisa": return pedir("revisa", onAbrirRevisa);
      case "bitacora": return onAbrirBienestar ? onAbrirBienestar() : pedir("bitacora", onAbrirBitacora);
      case "bibliografia": return pedir("bibliografia", onAbrirBibliografia);
      // MiPAE en turno, Benito y el calendario viven detrás de la sesión y
      // el perfil completo: la puerta es la tarjeta de acceso.
      default: return irAAcceso();
    }
  };

  const accionesDe = (id) => {
    if (id === "aprender") return [
      { label: "Abrir la Enciclopedia", primaria: true, onClick: () => abrirHerramienta("referencia") },
      { label: "Practicar en Modo estudio", onClick: () => abrirHerramienta("estudio") },
    ];
    if (id === "fundamentar") return [
      { label: "Ver el caso de ejemplo", primaria: true, onClick: () => verDemo(etapa === "interno" ? "interno" : "estudiante") },
      { label: "Crear mi PAE", conCuenta: true, onClick: () => abrirHerramienta("rapido") },
    ];
    if (id === "turno") return [
      { label: etapa === "tens" ? "Ver un registro de cuidados" : "Ver un registro de ejemplo", primaria: true, onClick: () => verDemo(etapa === "tens" ? "tens" : "enfermero") },
      { label: "Crear mi cuenta", onClick: irAAcceso },
    ];
    return [
      { label: "Abrir Escalas", primaria: true, onClick: () => abrirHerramienta("escalas") },
      { label: "Abrir Medicamentos", onClick: () => abrirHerramienta("medicamentos") },
    ];
  };

  return (
    <div ref={raiz} className="font-sans">

      {/* ── 1 · Portada: qué es, por qué sirve y el acceso ─────────────── */}
      <section aria-labelledby="portada-titulo" className="relative overflow-hidden bg-paper">
        {/* grid-cols-1 = minmax(0,1fr): sin eso la tira desplazable del
            teléfono ensancha la columna a su ancho total y la portada se
            corta por la derecha. */}
        <div className="mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-14 px-5 pb-14 pt-10 sm:pt-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)] lg:gap-16 lg:pb-24 lg:pt-16">
          <div className="min-w-0 text-center lg:text-left">
            <SaludoDieciocho />
            <h1 id="portada-titulo" className="pp-aparece mx-auto max-w-[16ch] font-serif text-28px font-semibold leading-[1.12] tracking-tight text-ink sm:text-38px lg:mx-0 lg:text-46px lg:leading-[1.08]">
              {IDENTIDAD_MIPAE.eslogan}
            </h1>
            {/* key + animate-cruce: al elegir etapa el texto se re-monta con un
                fundido corto, y como el selector está justo abajo se nota
                que respondió (antes vivía varios pliegues más abajo). */}
            <div className="pp-aparece" style={retraso(90)}>
            <p key={etapa || "default"} className="mx-auto mt-4 max-w-[36rem] text-body leading-relaxed text-ink-subtle dark:text-ink-muted animate-cruce sm:mt-5 sm:text-17px lg:mx-0 lg:text-18px">
              {landing.sub}
            </p>
            </div>

            <div className={`pp-aparece mt-8 ${COLUMNA}`} style={retraso(180)}>
              <p id="etapa-titulo" className="text-small font-medium text-ink-subtle dark:text-ink-muted">¿En qué etapa estás?</p>
              <div role="group" aria-labelledby="etapa-titulo" className="mt-2 grid grid-cols-4 gap-1 rounded-tk-md border border-edge bg-paper p-1">
                {ETAPAS.map((e) => {
                  const sel = etapa === e.id;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      aria-pressed={sel}
                      onClick={() => elegirEtapa(e.id)}
                      className={[
                        "min-h-[44px] rounded-tk-sm px-1 text-small transition-colors",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                        sel ? "bg-accent font-semibold text-paper" : "font-medium text-ink-subtle dark:text-ink-muted hover:bg-surface hover:text-ink",
                      ].join(" ")}
                    >
                      {e.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={`pp-aparece mt-6 ${COLUMNA}`} style={retraso(260)}>
              <div
                id="acceso-mipae"
                className={`scroll-mt-24 transition-opacity duration-150 ${accesoVisible ? "opacity-100" : "opacity-0"}`}
              >
                {acceso}
              </div>
            </div>

            <div className={`pp-aparece mt-3 flex flex-wrap items-center justify-center gap-x-4 lg:justify-start ${COLUMNA}`} style={retraso(320)}>
              <button
                type="button"
                onClick={() => { trackLanding("landing_cta", { etapa, cta: landing.ctaId }); landing.onCta?.(); }}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-tk-sm text-small font-semibold text-accent dark:text-accent-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {landing.cta}
                <ArrowRight aria-hidden="true" size={15} strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={onQueEsMiPAE}
                className="inline-flex min-h-[44px] items-center rounded-tk-sm text-small font-medium text-ink-subtle dark:text-ink-muted underline underline-offset-4 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                ¿Qué es MiPAE?
              </button>
              {/* Contacto en la primera pantalla (2026-10-30): una persona que
                  llegó a conocer MiPAE no encontró cómo escribirnos. Lleva a
                  Contacto, que ordena WhatsApp y correo por motivo. */}
              {onContacto && (
                <a
                  href="/contacto"
                  onClick={(e) => { e.preventDefault(); onContacto(); }}
                  className="inline-flex min-h-[44px] items-center rounded-tk-sm text-small font-medium text-ink-subtle dark:text-ink-muted underline underline-offset-4 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                >
                  Contacto
                </a>
              )}
            </div>

            {/* Badges oficiales de las tiendas, auto-hospedados en /brand porque
                la CSP bloquea los remotos (ver la historia en #664). Solo web:
                en la app instalada ofrecer descargarla es ruido.
                - App Store (publicada el 2026-10-11): arte oficial de Apple
                  Marketing Tools en español (es-MX), copiado byte a byte. La
                  guía de Apple exige la insignia negra cuando hay otras
                  tiendas, ponerla PRIMERO, no bajar de 40 px de alto, dejar
                  libre un cuarto de su alto alrededor y dar sus créditos.
                - Google Play: el PNG trae aire transparente arriba y abajo (la
                  insignia visible ocupa 192 de sus 250 px de alto), así que se
                  dibuja más alto para que las dos insignias VISIBLES midan lo
                  mismo, que es lo que piden las dos guías.
                Los altos van en px y no en la escala de Tailwind a propósito: el
                rem de la app es de 14,4 px, así que h-10 daba 36 px y dejaba la
                insignia de Apple bajo su mínimo de 40.
                El enlace crece hasta 44 px de alto (el mínimo táctil de la app) sin
                agrandar la insignia, y su nombre accesible es el texto que se lee
                en la insignia (el alt), sin aria-label que lo contradiga. El
                crédito nombra iPhone porque «¿Qué es MiPAE?» lo menciona.
                Ninguna de las dos guías permite redibujarlas ni recortarlas. */}
            {!isNative && (
              <div className={`pp-aparece mt-5 ${COLUMNA}`} style={retraso(360)}>
                <div className="flex flex-wrap items-center justify-center gap-4 lg:justify-start">
                  <a
                    href={URL_APP_STORE}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] items-center rounded-tk-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                    onClick={() => trackLanding("landing_cta", { etapa, cta: "app_store_badge" })}
                  >
                    <img
                      src="/brand/app-store-badge-es-mx.svg"
                      alt="Descárgalo en el App Store"
                      width={120}
                      height={40}
                      className="h-[40px] w-auto sm:h-[48px]"
                    />
                  </a>
                  <a
                    href={URL_GOOGLE_PLAY}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] items-center rounded-tk-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                    onClick={() => trackLanding("landing_cta", { etapa, cta: "google_play_badge" })}
                  >
                    <img
                      src="/brand/google-play-badge-es-419.png"
                      alt="Descargar en Google Play"
                      width={646}
                      height={250}
                      className="h-[52px] w-auto sm:h-[63px]"
                    />
                  </a>
                </div>
                <p className="mt-3 text-10px font-normal leading-snug tracking-normal text-ink-muted">
                  Apple, el logotipo de Apple e iPhone son marcas comerciales de Apple Inc., registradas en EE. UU. y otros países. App Store es una marca de servicio de Apple Inc., registrada en EE. UU. y otros países. Google Play y el logotipo de Google Play son marcas comerciales de Google LLC.
                </p>
              </div>
            )}
          </div>

          {/* La cara del producto: Benito, con su cara, respondiendo una duda
              de estudio, y dos resultados reales de la app al costado. Las
              piezas del costado repiten lo que las viñetas de abajo muestran
              completo, por eso van ocultas al lector de pantalla. */}
          <div className="pp-aparece relative mx-auto w-full min-w-0 max-w-[32rem] lg:max-w-[400px] xl:max-w-none" style={retraso(220)}>
            <div className="flex items-center gap-3 xl:ml-auto xl:mr-4 xl:w-[360px]">
              <span aria-hidden="true" className="grid h-11 w-11 flex-shrink-0 place-items-center overflow-hidden rounded-full border border-accent-border bg-accent-bg sm:h-14 sm:w-14">
                <BenitoAvatar size={40} />
              </span>
              <p className="text-small leading-snug">
                <span className="font-semibold text-ink">Benito</span>{" "}
                <span className={TEXTO_2}>es el compañero dentro de la app. Así responde una duda de estudio.</span>
              </p>
            </div>
            <VinetaBenito className="relative mt-4 sm:max-w-[360px] xl:ml-auto xl:mr-4" />
            {/* Solo con espacio real al costado (xl): a menos ancho tapaban el
                texto del chat. No se superponen al teléfono. */}
            <div aria-hidden="true" className="pp-flota absolute left-0 top-32 hidden w-48 rounded-tk-md border border-edge border-t-2 border-t-priority bg-paper p-3 shadow-tk-modal xl:block">
              <p className="text-small font-semibold text-ink-subtle dark:text-ink-muted">Diagnóstico prioritario</p>
              <p className="mt-1 font-serif text-body font-semibold leading-snug text-ink">{EJEMPLOS.pae.diagnostico}</p>
              <p className="mt-0.5 font-mono text-10.5px text-ink-subtle dark:text-ink-muted">NANDA · {EJEMPLOS.pae.codigo}</p>
            </div>
            <div aria-hidden="true" style={retraso(1700)} className="pp-flota absolute bottom-24 left-6 hidden w-44 rounded-tk-md border border-edge border-l-4 border-l-clinical-attention bg-paper p-3 shadow-tk-modal xl:block">
              <p className="text-small font-semibold text-ink-subtle dark:text-ink-muted">Escala de Braden</p>
              <p className="mt-1 text-22px font-semibold leading-none tabular-nums text-ink">{EJEMPLOS.braden.label}</p>
              <p className="mt-1 text-small font-semibold text-clinical-attention">{EJEMPLOS.braden.clasificacion}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 2 · Momentos: cómo se ve usarlo ────────────────────────────── */}
      <section id="momentos" aria-labelledby="momentos-titulo" className="scroll-mt-16 border-t border-edge bg-paper">
        <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-24">
          <div className={`pp-aparece max-w-[40rem] ${CENTRADO}`}>
            <h2 id="momentos-titulo" className={TITULO_SECCION}>
              ¿Qué necesitas hoy?
            </h2>
            <p className="mt-3 text-body leading-relaxed text-ink-subtle dark:text-ink-muted">
              Elige un momento y mira cómo se ve en MiPAE. Los ejemplos usan datos ficticios.
            </p>
          </div>
          <div className="pp-aparece mt-6 sm:mt-8" style={retraso(120)}>
            <MomentosPestanas activo={momento} onCambiar={setMomento} accionesDe={accionesDe} />
          </div>
        </div>
      </section>

      {/* ── 3 · Explora: qué se abre ya y qué pide cuenta ───────────────── */}
      <section aria-labelledby="explora-titulo" className="border-y border-edge bg-surface">
        <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-9 px-5 py-14 sm:gap-10 sm:py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,2.1fr)] lg:gap-16 lg:py-20">
          <div className={`pp-aparece ${CENTRADO}`}>
            <h2 id="explora-titulo" className={TITULO_SECCION}>
              Las herramientas de MiPAE
            </h2>
            <p className="mt-3 text-body leading-relaxed text-ink-subtle dark:text-ink-muted">
              Las escalas, los medicamentos y la Enciclopedia se abren ahora. Con tu cuenta gratuita sumas el PAE, MiPAE en turno, Benito y tu calendario de turnos.
            </p>
            <Button variant="primary" size="lg" onClick={irAAcceso} className="mt-6 min-h-[44px]">
              Crear mi cuenta gratuita
            </Button>
            {/* La invitación a las membresías (auditoría de conversión del
                25-oct-2026): la portada no decía que existían. Va aquí, junto
                a lo que trae la cuenta gratuita, como el siguiente escalón y no
                como un aviso aparte. SOLO web: en la app instalada esta portada
                también existe y ahí no se vende ni se enlaza a /precios
                (skill venta-solo-web). */}
            {!isNative && (
              <div className="mt-6 border-t border-edge pt-5">
                <p className="text-small leading-relaxed text-ink-subtle dark:text-ink-muted">
                  ¿Necesitas más? Con Estudiante Plus armas PAE sin cuota mensual y con Enfermero Plus tienes más evoluciones con IA para tus turnos. Las dos suman consultas a Benito.
                </p>
                <a
                  href="/precios"
                  onClick={() => trackLanding("landing_cta", { etapa, cta: "membresias" })}
                  className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-tk-sm text-small font-semibold text-accent dark:text-accent-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                >
                  Conoce las membresías
                  <ArrowRight aria-hidden="true" size={15} strokeWidth={2} />
                </a>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 sm:gap-8">
            <div>
              <h3 className="text-small font-semibold text-ink">Se abren sin cuenta</h3>
              <ul className="mt-2 divide-y divide-edge border-y border-edge">
                {INDICE_SIN_CUENTA.map((id, i) => <FilaHerramienta key={id} id={id} orden={i} onAbrir={abrirHerramienta} />)}
              </ul>
            </div>
            <div>
              <h3 className="text-small font-semibold text-ink">Con tu cuenta gratuita</h3>
              <ul className="mt-2 divide-y divide-edge border-y border-edge">
                {INDICE_CON_CUENTA.map((id, i) => <FilaHerramienta key={id} id={id} orden={i} onAbrir={abrirHerramienta} />)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4 · Por qué existe: propósito y valores ───────────────────────
          Los valores van por nombre en una frase; su texto completo vive en
          «¿Qué es MiPAE?». */}
      <BloqueProposito>
        <p className={`mt-5 text-body leading-relaxed ${TEXTO_2}`}>
          Nos guían seis valores: {fraseValores(IDENTIDAD_MIPAE.valores.map((v) => v.nombre))}.
        </p>
        <Button variant="secondary" size="lg" onClick={onQueEsMiPAE} iconRight={<ArrowRight aria-hidden="true" size={16} strokeWidth={2} />} className="mt-6 min-h-[44px]">
          Conoce la misión, la visión y los valores
        </Button>
      </BloqueProposito>

      {/* ── 5 · Qué pasa con lo que registras ─────────────────────────── */}
      <section aria-labelledby="confianza-titulo" className="border-t border-edge bg-paper">
        <div className="mx-auto max-w-[1240px] px-5 py-14 sm:py-16 lg:py-20">
          <h2 id="confianza-titulo" className={`pp-aparece ${TITULO_SECCION} ${CENTRADO}`}>
            Qué pasa con lo que registras
          </h2>
          <ul className="mt-6 grid grid-cols-1 gap-6 sm:mt-8 sm:grid-cols-3 sm:gap-8">
            {[
              { Icono: ShieldCheck, titulo: "Sin datos que identifiquen", texto: "MiPAE está pensado para casos sin nombres, RUT ni otros datos que identifiquen a una persona, en línea con la Ley 20.584." },
              { Icono: Clock, titulo: "El turno se borra al terminar", texto: "Lo que registras en MiPAE en turno se borra solo al terminar el turno." },
              { Icono: UserRound, titulo: "Tu cuenta es tuya", texto: "Tus PAE y tu Bitácora quedan en tu cuenta mientras la mantengas, y puedes eliminarla desde Mi perfil." },
            ].map(({ Icono, titulo, texto }, i) => (
              <li key={titulo} className="pp-aparece flex min-w-0 gap-3 sm:block" style={retraso(100 + i * 110)}>
                <Icono aria-hidden="true" size={22} strokeWidth={1.7} className="mt-0.5 flex-shrink-0 text-ink sm:mt-0" />
                <div className="min-w-0">
                  <p className="text-body font-semibold text-ink sm:mt-3">{titulo}</p>
                  <p className={`mt-1 text-small font-normal leading-relaxed sm:mt-1.5 ${TEXTO_2}`}>{texto}</p>
                </div>
              </li>
            ))}
          </ul>
          <div className={CENTRADO}>
          <a
            href="/privacidad"
            onClick={(e) => { e.preventDefault(); onPrivacidad?.(); }}
            className="mt-6 inline-flex min-h-[44px] items-center gap-1.5 rounded-tk-sm text-small font-semibold text-accent dark:text-accent-light hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
          >
            Lee la Política de Privacidad
            <ArrowRight aria-hidden="true" size={15} strokeWidth={2} />
          </a>
          </div>
        </div>
      </section>

      {/* ── 6 · MiPAE para instituciones (2026-10-30) ────────────────────
          Breve y con una invitación, "Si te interesa, puedes contactarnos",
          con WhatsApp (el mensaje institucional) y el correo. Solo web: en la app instalada no se ofrece
          nada que suene a venta (venta-solo-web). El detalle vive en
          /instituciones. */}
      {!isNative && (
        <section id="instituciones" aria-labelledby="instituciones-titulo" className="scroll-mt-16 border-t border-edge bg-paper">
          <div className="mx-auto grid max-w-[1240px] grid-cols-1 gap-6 px-5 py-14 sm:py-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:items-center lg:gap-16 lg:py-20">
            <div className={`pp-aparece ${CENTRADO}`}>
              <Building2 aria-hidden="true" size={26} strokeWidth={1.6} className="mx-auto text-ink lg:mx-0" />
              <h2 id="instituciones-titulo" className={`mt-3 ${TITULO_SECCION}`}>
                MiPAE para instituciones
              </h2>
            </div>
            <div className={`pp-aparece ${CENTRADO}`} style={retraso(120)}>
              <p className={`text-body leading-relaxed ${TEXTO_2}`}>
                Para clínicas, hospitales, universidades y organizaciones de enfermería. Te mostramos MiPAE con casos ficticios y vemos juntos una implementación o una alianza.
              </p>
              <InvitacionInstitucional ubicacion="portada" fila="justify-center lg:justify-start" className="mt-5" />
              {onInstituciones && (
                <a
                  href="/instituciones"
                  onClick={(e) => { e.preventDefault(); onInstituciones(); }}
                  className={`mt-2 ${ENLACE_TEXTO}`}
                >
                  Qué ofrece MiPAE a una institución
                  <ArrowRight aria-hidden="true" size={15} strokeWidth={2} />
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── 7 · Quién está detrás, con la firma del autor ─────────────── */}
      <section aria-labelledby="cierre-titulo" className="border-t border-edge bg-surface">
        <div className="pp-aparece mx-auto max-w-[44rem] px-5 py-14 text-center sm:py-16 lg:py-20">
          <SelloAlLlegar size={80} />
          <h2 id="cierre-titulo" className={`mt-5 ${TITULO_SECCION}`}>
            Quién está detrás
          </h2>
          <p className={`mx-auto mt-3 max-w-[34rem] text-body leading-relaxed ${TEXTO_2}`}>
            MiPAE lo creó Sebastián Undurraga, enfermero chileno, a partir de necesidades que conoció estudiando y ejerciendo. Hoy lo construimos junto a quienes lo usan.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Button variant="primary" size="lg" onClick={irAAcceso} className="min-h-[44px]">
              Crear mi cuenta gratuita
            </Button>
            <Button variant="secondary" size="lg" onClick={onQueEsMiPAE} className="min-h-[44px]">
              ¿Qué es MiPAE?
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
